import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, Paper,
} from '@mui/material';
import { ReactFlowProvider } from 'reactflow';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  GridOn as GridIcon,
  GridOff as GridOffIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import FlowNodeMCP from './FlowNodeMCP';
import { useFlowContext } from '../../contexts/FlowContext.jsx';
import EdgeDialog from './EdgeDialog';
import EdgeContextMenu from './EdgeContextMenu';

const NODE_WIDTH = 200; // Increased from 180
const NODE_HEIGHT = 140; // Increased from 120

function FlowCanvas({
  nodes,
  edges,
  breakpoints = [],
  onNodeSelect,
  selectedNode,
  onNodeDelete,
  onCanvasClick,
  onNodeConnect,
  connectionStart,
  setConnectionStart,
  onNodeDrag,
  onNodeConfigUpdate,
  updateNode,
  onEdgeDelete,
  onEdgeUpdate,
  onAddNode,
}) {
  // Debug logging for nodes (commented out to reduce console clutter)
  // console.log('🎨 FlowCanvas received nodes:', nodes.length);
  // console.log('🎨 Node IDs in FlowCanvas:', nodes.map(n => n.id));
  // console.log('🎨 Node types in FlowCanvas:', nodes.map(n => n.type));
  // console.log('🎨 Node positions in FlowCanvas:', nodes.map(n => ({ id: n.id, type: n.type, position: n.position })));

  // Debug: Find code-executor nodes specifically
  const codeExecutorNodes = nodes.filter((n) => n.type === 'code-executor');
  // console.log('🔍 Code-executor nodes found:', codeExecutorNodes.length);
  // codeExecutorNodes.forEach((node, index) => {
  //   console.log(`🔍 Code-executor ${index + 1}:`, node.id, 'at position:', node.position);
  // });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [edgeDialogOpen, setEdgeDialogOpen] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 2000, height: 2000 });

  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    open: false,
    anchorEl: null,
    edge: null,
  });

  // Hover state for edges
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const canvasRef = useRef(null);
  const { setVar, setCurrentNode } = useFlowContext();

  // Monitor zoom and pan changes for debugging
  useEffect(() => {
    // Zoom/pan changed - debugging commented out to reduce console clutter
    // console.log('=== ZOOM/PAN CHANGED ===');
    // console.log('Zoom:', zoom);
    // console.log('Pan:', pan);
    // console.log('ShowGrid:', showGrid);
    // console.log('========================');
  }, [zoom, pan, showGrid]);

  // Enhanced mouse handling for panning
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (connectionStart && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left - pan.x) / zoom,
          y: (e.clientY - rect.top - pan.y) / zoom,
        });
      } else if (isPanning) {
        const deltaX = e.clientX - lastPanPoint.x;
        const deltaY = e.clientY - lastPanPoint.y;
        setPan((prev) => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        }));
        setLastPanPoint({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      setIsPanning(false);
    };

    if (connectionStart || isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [connectionStart, zoom, isPanning, lastPanPoint, pan]);

  // Handle drop events from palette
  const handleCanvasDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      // Try multiple data formats
      let itemData = e.dataTransfer.getData('application/json');

      if (!itemData) {
        itemData = e.dataTransfer.getData('text/plain');
      }

      if (!itemData) {
        itemData = e.dataTransfer.getData('text/html');
      }

      if (!itemData) {
        return;
      }

      const item = JSON.parse(itemData);

      // Calculate drop position relative to canvas
      const rect = canvasRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      // Calculate position relative to the canvas container
      const relativeX = e.clientX - rect.left;
      const relativeY = e.clientY - rect.top;

      // Adjust for pan and zoom to get the actual canvas coordinates
      const x = (relativeX - pan.x) / zoom;
      const y = (relativeY - pan.y) / zoom;

      // Ensure the position is within reasonable bounds
      const boundedX = Math.max(0, Math.min(x, 1000));
      const boundedY = Math.max(0, Math.min(y, 1000));

      // Call the onAddNode callback with position
      if (onAddNode) {
        onAddNode(item, { x: boundedX, y: boundedY });
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  }, [pan, zoom, onAddNode]);

  const handleCanvasDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleCanvasDragLeave = useCallback((e) => {
    setIsDragOver(false);
  }, []);

  // Enhanced canvas click handling
  const handleCanvasClick = (e) => {
    // Canvas click handling - debugging commented out to reduce console clutter
    // console.log('=== CANVAS CLICK ===');
    // console.log('Canvas clicked, target:', e.target);
    // console.log('Current target:', e.currentTarget);
    // console.log('Target tag name:', e.target.tagName);
    // console.log('Target class name:', e.target.className);
    // console.log('========================');

    // Only handle canvas clicks if clicking directly on canvas or SVG edges
    if (e.target === e.currentTarget || e.target === canvasRef.current || e.target.tagName === 'svg') {
      if (connectionStart) {
        setConnectionStart(null);
        setConnectionSuccess(false);
      } else {
        setSelectedEdge(null);
        onCanvasClick(e);
      }
    }
  };

  // Enhanced mouse down for panning
  const handleCanvasMouseDown = (e) => {
    // Canvas mouse down handling - debugging commented out to reduce console clutter
    // console.log('=== CANVAS MOUSE DOWN ===');
    // console.log('Button:', e.button);
    // console.log('Alt key:', e.altKey);
    // console.log('Target:', e.target);
    // console.log('========================');

    // Only handle panning if clicking directly on canvas, not on nodes or edges
    if (e.target === e.currentTarget || e.target === canvasRef.current) {
      // Middle click (button 1) or Alt + Left click (button 0)
      if (e.button === 1 || (e.button === 0 && e.altKey)) {
        console.log('Starting canvas pan');
        e.preventDefault();
        setIsPanning(true);
        setLastPanPoint({ x: e.clientX, y: e.clientY });
      }
    }
  };

  // Enhanced mouse move for panning
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isPanning && lastPanPoint) {
        const deltaX = e.clientX - lastPanPoint.x;
        const deltaY = e.clientY - lastPanPoint.y;

        setPan((prev) => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        }));

        setLastPanPoint({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      if (isPanning) {
        console.log('Stopping canvas pan');
        setIsPanning(false);
        setLastPanPoint(null);
      }
    };

    if (isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanning, lastPanPoint]);

  // Enhanced zoom controls
  const handleZoomIn = () => {
    console.log('Zoom In clicked, current zoom:', zoom);
    const newZoom = Math.min(zoom + 0.1, 2);
    console.log('Setting zoom to:', newZoom);
    setZoom(newZoom);
  };

  const handleZoomOut = () => {
    console.log('Zoom Out clicked, current zoom:', zoom);
    const newZoom = Math.max(zoom - 0.1, 0.3);
    console.log('Setting zoom to:', newZoom);
    setZoom(newZoom);
  };

  const handleFitScreen = () => {
    console.log('Fit Screen clicked');
    if (nodes.length === 0) return;

    const bounds = getBounds();
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) return;

    // Calculate scale to fit all nodes in the viewport
    const contentWidth = bounds.maxX - bounds.minX;
    const contentHeight = bounds.maxY - bounds.minY;
    const scaleX = (canvasRect.width - 100) / contentWidth;
    const scaleY = (canvasRect.height - 100) / contentHeight;
    const newZoom = Math.min(scaleX, scaleY, 1);

    console.log('Fit screen - setting zoom to:', newZoom);
    console.log('Canvas bounds:', bounds);
    console.log('Canvas rect:', canvasRect);
    console.log('Content dimensions:', { width: contentWidth, height: contentHeight });
    console.log('Scale factors:', { scaleX, scaleY });
    setZoom(newZoom);
    setPan({
      x: 50 - bounds.minX * newZoom,
      y: 50 - bounds.minY * newZoom,
    });
  };

  // Calculate bounds for fit screen
  const getBounds = () => {
    if (!nodes.length) {
      return {
        minX: 0, minY: 0, maxX: 2000, maxY: 2000,
      };
    }

    const minX = Math.min(...nodes.map((n) => n.position.x));
    const minY = Math.min(...nodes.map((n) => n.position.y));
    const maxX = Math.max(...nodes.map((n) => n.position.x + NODE_WIDTH));
    const maxY = Math.max(...nodes.map((n) => n.position.y + NODE_HEIGHT));

    return {
      minX, minY, maxX, maxY,
    };
  };

  // Calculate dynamic canvas size based on nodes
  const getCanvasSize = () => {
    if (nodes.length === 0) {
      return { width: 2000, height: 2000 };
    }

    const bounds = getBounds();
    const padding = 400; // Extra padding around nodes

    return {
      width: Math.max(2000, bounds.maxX - bounds.minX + padding),
      height: Math.max(2000, bounds.maxY - bounds.minY + padding),
    };
  };

  // Recalculate canvas size when nodes change
  useEffect(() => {
    const newSize = getCanvasSize();
    console.log('Nodes changed, recalculating canvas size');
    console.log('New canvas size:', newSize);
    setCanvasSize(newSize);
  }, [nodes]);

  // Enhanced node click handling
  const handleNodeClick = (node, event, isDoubleClick = false, clickMode = 'select') => {
    console.log('FlowCanvas handleNodeClick:', node.id, isDoubleClick, clickMode);
    event.stopPropagation();

    // Set current node in context
    if (setCurrentNode) {
      setCurrentNode(node.id);
    }

    // Handle connection mode
    if (clickMode === 'connect') {
      console.log('=== CONNECTION MODE HANDLING ===');
      console.log('Click mode:', clickMode);
      console.log('Connection start:', connectionStart);
      console.log('Current node:', node.id);

      if (!connectionStart) {
        console.log('Setting connection start to:', node.id);
        setConnectionStart(node);
        setConnectionSuccess(false);
      } else if (connectionStart.id !== node.id) {
        console.log('Attempting to connect:', connectionStart.id, 'to', node.id);
        const success = onNodeConnect(connectionStart, node);
        console.log('Connection result:', success);
        if (success !== false) {
          setConnectionSuccess(true);
          setTimeout(() => {
            setConnectionStart(null);
            setConnectionSuccess(false);
          }, 500);
        } else {
          setConnectionStart(null);
          setConnectionSuccess(false);
        }
      } else {
        console.log('Canceling connection - same node clicked');
        setConnectionStart(null);
        setConnectionSuccess(false);
      }
      return;
    }

    // Handle node selection and configuration
    if (isDoubleClick || clickMode === 'config') {
      console.log('Double click detected, opening config for node:', node.id);
      onNodeSelect(node, event, true, 'config');
    } else {
      console.log('Single click detected, selecting node:', node.id);
      onNodeSelect(node, event, false, 'select');
    }
  };

  // Enhanced node drag handling
  const handleNodeDrag = (nodeId, newPosition) => {
    console.log('Node drag detected:', nodeId, newPosition);
    if (onNodeDrag) {
      onNodeDrag(nodeId, newPosition);
    }
  };

  // Enhanced node delete handling
  const handleNodeDelete = (nodeId) => {
    console.log('Node delete detected:', nodeId);
    if (onNodeDelete) {
      onNodeDelete(nodeId);
    }
  };

  // Enhanced node config update handling
  const handleNodeConfigUpdate = (nodeId, config) => {
    console.log('Node config update detected:', nodeId, config);
    if (onNodeConfigUpdate) {
      onNodeConfigUpdate(nodeId, config);
    }
  };

  // Enhanced edge handling
  const handleEdgeClick = (edge, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Set selected edge for keyboard operations
    setSelectedEdge(edge);

    // Show edge context menu on right-click
    if (e.button === 2 || e.ctrlKey) {
      handleEdgeContextMenu(edge, e);
    }

    console.log('Edge clicked:', edge);
  };

  const handleEdgeContextMenu = (edge, e) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      open: true,
      anchorEl: e.currentTarget,
      edge,
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu({
      open: false,
      anchorEl: null,
      edge: null,
    });
  };

  const handleContextMenuEdit = (edge) => {
    setSelectedEdge(edge);
    setEdgeDialogOpen(true);
    handleContextMenuClose();
  };

  const handleContextMenuDelete = (edge) => {
    if (onEdgeDelete) {
      onEdgeDelete(edge);
      setSelectedEdge(null);
    }
    handleContextMenuClose();
  };

  const handleContextMenuCopy = (edge) => {
    navigator.clipboard.writeText(JSON.stringify(edge, null, 2));
    handleContextMenuClose();
  };

  const handleEdgeDoubleClick = (edge) => {
    setSelectedEdge(edge);
    setEdgeDialogOpen(true);
  };

  // Enhanced keyboard handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      const tag = document.activeElement.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      switch (event.key) {
        case 'Delete':
        case 'Backspace':
          if (selectedEdge && onEdgeDelete) {
            event.preventDefault();
            onEdgeDelete(selectedEdge);
            setSelectedEdge(null);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setSelectedEdge(null);
          setConnectionStart(null);
          break;
        case 'Tab':
          if (event.shiftKey) {
            event.preventDefault();
            // Navigate to previous edge
            const currentIndex = edges.findIndex((e) => e.id === selectedEdge?.id);
            if (currentIndex > 0) {
              setSelectedEdge(edges[currentIndex - 1]);
            } else if (edges.length > 0) {
              setSelectedEdge(edges[edges.length - 1]);
            }
          } else {
            event.preventDefault();
            // Navigate to next edge
            const currentIndex = edges.findIndex((e) => e.id === selectedEdge?.id);
            if (currentIndex < edges.length - 1) {
              setSelectedEdge(edges[currentIndex + 1]);
            } else if (edges.length > 0) {
              setSelectedEdge(edges[0]);
            }
          }
          break;
        case 'Enter':
          if (selectedEdge) {
            event.preventDefault();
            setEdgeDialogOpen(true);
          }
          break;
        case '=':
        case '+':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleZoomIn();
          }
          break;
        case '-':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleZoomOut();
          }
          break;
        case '0':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleFitScreen();
          }
          break;
        case 'g':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setShowGrid(!showGrid);
          }
          break;
        case 'e':
          if (event.ctrlKey || event.metaKey && selectedEdge) {
            event.preventDefault();
            setEdgeDialogOpen(true);
          }
          break;
        case 'c':
          if (event.ctrlKey || event.metaKey && selectedEdge) {
            event.preventDefault();
            navigator.clipboard.writeText(JSON.stringify(selectedEdge, null, 2));
          }
          break;
        case 'E':
          if (event.shiftKey) {
            event.preventDefault();
            // Toggle edge selection mode (removed - no longer needed)
            console.log('Edge selection mode removed - use double-click on edges to configure');
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [selectedEdge, onEdgeDelete, showGrid, edges]);

  const getEdgeStyle = (edge) => {
    const isSelected = selectedEdge?.id === edge.id;
    const isHovered = hoveredEdge === edge.id;

    // Enhanced edge type info with visual indicators
    const edgeTypeInfo = {
      'edge-basic': {
        color: '#1976d2',
        width: 2,
        dash: 'none',
        icon: '→',
        description: 'Direct connection',
      },
      'edge-conditional': {
        color: '#9c27b0',
        width: 2,
        dash: '5,5',
        icon: '🔀',
        description: 'Conditional routing',
        animation: 'pulse 2s infinite',
      },
      'edge-success': {
        color: '#4caf50',
        width: 3,
        dash: 'none',
        icon: '✅',
        description: 'Success path',
        shadow: '0 0 8px rgba(76, 175, 80, 0.5)',
      },
      'edge-error': {
        color: '#f44336',
        width: 3,
        dash: '10,5',
        icon: '❌',
        description: 'Error handling',
        animation: 'shake 0.5s infinite',
      },
      'edge-always': {
        color: '#ff9800',
        width: 2,
        dash: 'none',
        icon: '🔄',
        description: 'Always execute',
      },
      'edge-parallel': {
        color: '#2196f3',
        width: 2,
        dash: '3,3',
        icon: '⚡',
        description: 'Parallel execution',
      },
      'edge-join': {
        color: '#673ab7',
        width: 3,
        dash: '5,5',
        icon: '🔗',
        description: 'Join parallel branches',
      },
      'edge-loop': {
        color: '#e91e63',
        width: 2,
        dash: '8,4',
        icon: '🔁',
        description: 'Loop back',
        animation: 'spin 3s linear infinite',
      },
      'edge-timeout': {
        color: '#ff5722',
        width: 2,
        dash: '15,5',
        icon: '⏱️',
        description: 'Timeout handling',
      },
      'edge-retry': {
        color: '#795548',
        width: 2,
        dash: '2,2',
        icon: '🔄',
        description: 'Retry on failure',
      },
    };

    const typeInfo = edgeTypeInfo[edge.type] || edgeTypeInfo['edge-basic'];

    return {
      strokeColor: typeInfo.color,
      strokeWidth: isSelected ? typeInfo.width + 2 : isHovered ? typeInfo.width + 1 : typeInfo.width,
      strokeDasharray: typeInfo.dash === 'none' ? 'none' : typeInfo.dash,
      markerEnd: 'url(#arrowhead)',
      filter: isSelected ? 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8))' : 'none',
      animation: typeInfo.animation || 'none',
      // Store metadata for tooltips and statistics
      metadata: {
        icon: typeInfo.icon,
        description: typeInfo.description,
        executionCount: edge.executionCount || 0,
        lastExecution: edge.lastExecution || null,
        successRate: edge.successRate || 0,
      },
    };
  };

  // Enhanced edge rendering with visual indicators and statistics
  const renderEdge = useCallback((edge) => {
    const edgeType = edge.type || 'edge-basic';
    const isConditional = edgeType.includes('conditional') || edgeType.includes('success') || edgeType.includes('error');
    const hasCondition = edge.condition && edge.condition.trim() !== '';

    // Get enhanced edge style
    const edgeStyleData = getEdgeStyle(edge);

    const edgeStyle = {
      stroke: edgeStyleData.strokeColor,
      strokeWidth: edgeStyleData.strokeWidth,
      strokeDasharray: edgeStyleData.strokeDasharray,
      animation: edgeStyleData.animation,
      filter: edgeStyleData.filter,
    };

    // Enhanced edge label with statistics
    const { metadata } = edgeStyleData;
    const executionStats = metadata.executionCount > 0 ? (
      <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
        {metadata.executionCount}
        {' '}
        exec •
        {metadata.successRate}
        % success
      </div>
    ) : null;

    const edgeLabel = (
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        color: '#333',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '500',
        border: `2px solid ${edgeStyleData.strokeColor}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
        minWidth: '80px',
        textAlign: 'center',
      }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          marginBottom: '2px',
        }}
        >
          <span style={{ fontSize: '12px' }}>{metadata.icon}</span>
          <span>{edge.label || edgeType.replace('edge-', '').replace('-', ' ')}</span>
        </div>
        {hasCondition && (
          <div style={{
            fontSize: '9px',
            color: '#666',
            marginBottom: '2px',
            fontFamily: 'monospace',
          }}
          >
            {edge.condition.length > 20 ? `${edge.condition.substring(0, 20)}...` : edge.condition}
          </div>
        )}
        {executionStats}
        <div style={{
          fontSize: '8px',
          color: '#999',
          marginTop: '2px',
        }}
        >
          {metadata.description}
        </div>
      </div>
    );

    return (
      <div key={edge.id} style={{ position: 'relative' }}>
        {/* Edge component - legacy code, using ReactFlow's built-in edges instead */}
        {/* <Edge
          id={edge.id}
          source={edge.source}
          target={edge.target}
          style={edgeStyle}
          label={edgeLabel}
          labelStyle={{
            background: 'transparent',
            border: 'none',
            color: 'transparent',
          }}
          labelBgStyle={{
            background: 'transparent',
            border: 'none',
          }}
          labelShowBg={false}
          type="smoothstep"
          animated={edgeType !== 'edge-basic'}
          className={`enhanced-edge ${edgeType.replace('-', '_')}`}
        /> */}

        {/* Enhanced visual indicator badge */}
        <div style={{
          position: 'absolute',
          top: '-12px',
          right: '-12px',
          background: edgeStyleData.strokeColor,
          color: 'white',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 'bold',
          border: '2px solid white',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          zIndex: 1001,
          animation: edgeStyleData.animation,
        }}
        >
          {metadata.icon}
        </div>
      </div>
    );
  }, [getEdgeStyle]);

  // Enhanced node rendering with conditional flow indicators
  const renderNode = useCallback((node) => {
    const nodeType = node.type || 'default';
    const isConditionalNode = nodeType === 'conditional' || nodeType === 'decision-tree' || nodeType === 'switch';
    const hasConditionalEdges = edges.some((edge) => edge.source === node.id && (edge.type?.includes('conditional') || edge.condition));

    // Enhanced node styling for conditional flows
    const nodeStyle = {
      border: isConditionalNode ? '3px solid #FF9800' : hasConditionalEdges ? '2px solid #FF9800' : '2px solid #1976d2',
      borderRadius: isConditionalNode ? '12px' : '8px',
      background: isConditionalNode ? 'linear-gradient(135deg, #FFF3E0, #FFE0B2)' : 'white',
      boxShadow: isConditionalNode ? '0 4px 8px rgba(255, 152, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
      animation: isConditionalNode ? 'conditionalNodePulse 2s infinite' : 'none',
    };

    return (
      <div key={node.id} style={{ position: 'relative' }}>
        {/* Node component - legacy code, using ReactFlow's built-in nodes instead - commented out */}
        {/* <Node
          id={node.id}
          data={node.data}
          position={node.position}
          style={nodeStyle}
          className={isConditionalNode ? 'conditional-node' : hasConditionalEdges ? 'conditional-flow-node' : 'normal-node'}
        /> */}

        {/* Conditional flow indicator */}
        {(isConditionalNode || hasConditionalEdges) && (
          <div style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#FF9800',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '10px',
            fontWeight: 'bold',
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 1001,
            whiteSpace: 'nowrap',
          }}
          >
            {isConditionalNode ? 'Conditional' : 'Conditional Flow'}
          </div>
        )}

        {/* True/False branch indicators for conditional nodes */}
        {isConditionalNode && (
          <>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '10px',
              background: '#4CAF50',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '8px',
              fontWeight: 'bold',
              border: '1px solid #4CAF50',
            }}
            >
              TRUE
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              background: '#F44336',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '8px',
              fontWeight: 'bold',
              border: '1px solid #F44336',
            }}
            >
              FALSE
            </div>
          </>
        )}
      </div>
    );
  }, [edges]);

  return (
    <ReactFlowProvider>
      {/* Enhanced Canvas */}
      <Box
        ref={canvasRef}
        onDrop={handleCanvasDrop}
        onDragOver={handleCanvasDragOver}
        onDragLeave={handleCanvasDragLeave}
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'auto', // Allow scrolling when content exceeds container
          overflowX: 'scroll', // Always show horizontal scrollbar
          overflowY: 'scroll', // Always show vertical scrollbar
          bgcolor: isDragOver ? 'primary.lighter' : 'background.default',
          border: 1,
          borderColor: isDragOver ? 'primary.main' : 'divider',
          cursor: isPanning ? 'grabbing' : 'grab',
          transition: 'all 0.2s ease',
          minHeight: 0, // Allow flex shrinking
          // Force scrollbars to always be visible
          '&::-webkit-scrollbar': {
            width: '12px',
            height: '12px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '6px',
            '&:hover': {
              background: 'rgba(0,0,0,0.3)',
            },
          },
        }}
        onMouseDown={handleCanvasMouseDown}
        onClick={handleCanvasClick}
      >
        {/* Canvas Header with Mode Indicator */}
        <Box
          onDragOver={handleCanvasDragOver}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            p: 1,
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'all 0.3s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Flow Canvas
            </Typography>
            {connectionStart && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(25, 118, 210, 0.1)',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'primary.main',
              }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  Connecting from:
                  {' '}
                  {connectionStart.data?.label || connectionStart.id}
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main' }}>
                  → Ctrl/Cmd + Click target node
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={handleZoomIn} title="Zoom In">
              <ZoomInIcon />
            </IconButton>
            <IconButton size="small" onClick={handleZoomOut} title="Zoom Out">
              <ZoomOutIcon />
            </IconButton>
            <IconButton size="small" onClick={handleFitScreen} title="Fit to Screen">
              <FitScreenIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                console.log('Grid toggle clicked, current showGrid:', showGrid);
                setShowGrid(!showGrid);
                console.log('Setting showGrid to:', !showGrid);
              }}
              title={showGrid ? 'Hide Grid' : 'Show Grid'}
            >
              {showGrid ? <GridOffIcon /> : <GridIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Canvas Size Indicator (for debugging) */}
        <Box
          sx={{
            position: 'absolute',
            top: 50,
            left: 8,
            zIndex: 100,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: 'monospace',
          }}
        >
          Canvas:
          {' '}
          {canvasSize.width}
          {' '}
          ×
          {' '}
          {canvasSize.height}
          <br />
          Nodes:
          {' '}
          {nodes.length}
        </Box>

        {/* Flow Direction Legend */}
        <Box
          sx={{
            position: 'absolute',
            top: 50,
            right: 8,
            zIndex: 100,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid',
            borderColor: 'divider',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            boxShadow: 2,
          }}
        >
          <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
            🔄 Flow Direction
          </Typography>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
          }}
          >
            <Box sx={{ width: 20, height: 2, bgcolor: 'primary.main' }} />
            <Typography variant="caption">→</Typography>
            <Typography variant="caption" color="text.secondary">
              Data Flow
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 20, height: 2, bgcolor: 'white', border: '1px dashed #ccc',
            }}
            />
            <Typography variant="caption">→</Typography>
            <Typography variant="caption" color="text.secondary">
              Animated Flow
            </Typography>
          </Box>
        </Box>

        {/* SVG Container - expands to accommodate canvas size */}
        <Box
          onDrop={handleCanvasDrop}
          onDragOver={handleCanvasDragOver}
          onDragLeave={handleCanvasDragLeave}
          sx={{
            position: 'relative',
            width: canvasSize.width,
            height: canvasSize.height,
            minWidth: '100%',
            minHeight: '100%',
          }}
        >
          <svg
            width="100%"
            height="100%"
            onDrop={handleCanvasDrop}
            onDragOver={handleCanvasDragOver}
            onDragLeave={handleCanvasDragLeave}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1,
              backgroundImage: showGrid ? `
                linear-gradient(rgba(128,128,128,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(128,128,128,0.1) 1px, transparent 1px)
              ` : 'none',
              backgroundSize: '20px 20px',
              transition: 'background-image 0.3s ease',
            }}
            ref={(ref) => {
            // Add CSS animation for flow direction
              if (ref) {
                const style = document.createElement('style');
                style.textContent = `
                @keyframes flowDirection {
                  0% { stroke-dashoffset: 0; }
                  100% { stroke-dashoffset: -10; }
                }
              `;
                document.head.appendChild(style);
              }
            }}
          >
            <defs>
              {/* Enhanced arrow markers with better visibility */}
              <marker
                id="arrowhead-basic"
                markerWidth="16"
                markerHeight="12"
                refX="15"
                refY="6"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L0,12 L16,6 z"
                  fill="#1976d2"
                  stroke="#1976d2"
                  strokeWidth="1.5"
                />
              </marker>

              <marker
                id="arrowhead-success"
                markerWidth="16"
                markerHeight="12"
                refX="15"
                refY="6"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L0,12 L16,6 z"
                  fill="#4CAF50"
                  stroke="#4CAF50"
                  strokeWidth="1.5"
                />
              </marker>

              <marker
                id="arrowhead-error"
                markerWidth="16"
                markerHeight="12"
                refX="15"
                refY="6"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L0,12 L16,6 z"
                  fill="#F44336"
                  stroke="#F44336"
                  strokeWidth="1.5"
                />
              </marker>

              <marker
                id="arrowhead-conditional"
                markerWidth="16"
                markerHeight="12"
                refX="15"
                refY="6"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L0,12 L16,6 z"
                  fill="#FF9800"
                  stroke="#FF9800"
                  strokeWidth="1.5"
                />
              </marker>

              {/* Flow direction arrow for all edges */}
              <marker
                id="arrowhead-flow"
                markerWidth="18"
                markerHeight="14"
                refX="17"
                refY="7"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L0,14 L18,7 z"
                  fill="#1976d2"
                  stroke="#1976d2"
                  strokeWidth="2"
                />
              </marker>

              {/* Enhanced gradients */}
              <linearGradient id="edgeGradient-basic" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1976d2" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#1976d2" stopOpacity="1" />
              </linearGradient>

              <linearGradient id="edgeGradient-success" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#4CAF50" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#4CAF50" stopOpacity="1" />
              </linearGradient>

              <linearGradient id="edgeGradient-error" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F44336" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#F44336" stopOpacity="1" />
              </linearGradient>

              <linearGradient id="edgeGradient-conditional" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#FF9800" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#FF9800" stopOpacity="1" />
              </linearGradient>

              {/* Enhanced filters */}
              <filter id="edgeShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="1" dy="1" stdDeviation="1" floodOpacity="0.3" />
              </filter>

              <filter id="edgeGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              <filter id="edgeSelected" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#1976d2" floodOpacity="0.6" />
              </filter>
            </defs>
            <g
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
              }}
            >
              {/* Debug info - commented out to reduce console clutter */}
              {/* {console.log('Canvas transform - pan:', pan, 'zoom:', zoom)} */}
              {/* Enhanced edges with better visual feedback */}
              {(() => edges.map((edge, i) => {
                const sourceNode = nodes.find((n) => n.id === edge.source);
                const targetNode = nodes.find((n) => n.id === edge.target);
                if (!sourceNode || !targetNode) return null;

                // Calculate coordinates in original space (SVG transform handles zoom/pan)
                const sourceX = sourceNode.position.x + NODE_WIDTH / 2;
                const sourceY = sourceNode.position.y + NODE_HEIGHT / 2;
                const targetX = targetNode.position.x + NODE_WIDTH / 2;
                const targetY = targetNode.position.y + NODE_HEIGHT / 2;

                // Calculate path
                const dx = targetX - sourceX;
                const dy = targetY - sourceY;
                const distance = Math.sqrt(dx * dx + dy * dy);
                const angle = Math.atan2(dy, dx);

                // Adjust endpoints to node boundaries (in original space)
                const nodeRadius = 20;
                const adjustedSourceX = sourceX + (sourceX < targetX ? nodeRadius : -nodeRadius);
                const adjustedSourceY = sourceY + (sourceY < targetY ? nodeRadius : -nodeRadius);
                const adjustedTargetX = targetX + (targetX > sourceX ? -nodeRadius : nodeRadius);
                const adjustedTargetY = targetY + (targetY > sourceY ? -nodeRadius : nodeRadius);

                const isSelected = selectedEdge?.id === edge.id;
                const edgeStyle = getEdgeStyle(edge);

                return (
                  <g key={edge.id} style={{ cursor: 'pointer' }}>
                    {/* Invisible click area for easier selection */}
                    <path
                      d={`M ${adjustedSourceX} ${adjustedSourceY} L ${adjustedTargetX} ${adjustedTargetY}`}
                      stroke="transparent"
                      strokeWidth={20}
                      fill="none"
                      onClick={(e) => handleEdgeClick(edge, e)}
                      onDoubleClick={(e) => handleEdgeDoubleClick(edge)}
                      onContextMenu={(e) => handleEdgeContextMenu(edge, e)}
                      onMouseEnter={() => setHoveredEdge(edge.id)}
                      onMouseLeave={() => setHoveredEdge(null)}
                    />

                    {/* Edge path with enhanced flow direction */}
                    <path
                      d={`M ${adjustedSourceX} ${adjustedSourceY} L ${adjustedTargetX} ${adjustedTargetY}`}
                      stroke={edgeStyle.strokeColor}
                      strokeWidth={edgeStyle.strokeWidth}
                      strokeDasharray={edgeStyle.strokeDasharray}
                      fill="none"
                      markerEnd="url(#arrowhead-flow)"
                      style={{
                        filter: isSelected ? 'drop-shadow(0 0 4px rgba(25, 118, 210, 0.5))'
                          : hoveredEdge === edge.id ? 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.3))' : 'none',
                        transition: 'all 0.2s ease',
                        pointerEvents: 'none', // Let the invisible path handle clicks
                      }}
                    />

                    {/* Flow direction indicator - animated arrow along the path */}
                    <path
                      d={`M ${adjustedSourceX} ${adjustedSourceY} L ${adjustedTargetX} ${adjustedTargetY}`}
                      stroke="rgba(255, 255, 255, 0.8)"
                      strokeWidth={2}
                      fill="none"
                      strokeDasharray="5,5"
                      style={{
                        animation: 'flowDirection 2s linear infinite',
                        pointerEvents: 'none',
                      }}
                    />

                    {/* Edge label */}
                    {(edge.label || edge.type !== 'edge-basic') && (
                    <g>
                      <rect
                        x={(adjustedSourceX + adjustedTargetX) / 2 - 40}
                        y={(adjustedSourceY + adjustedTargetY) / 2 - 12}
                        width={80}
                        height={24}
                        fill="white"
                        stroke={edgeStyle.strokeColor}
                        strokeWidth={1}
                        rx={4}
                        style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
                      />
                      <text
                        x={(adjustedSourceX + adjustedTargetX) / 2}
                        y={(adjustedSourceY + adjustedTargetY) / 2 + 2}
                        textAnchor="middle"
                        fontSize={10}
                        fill={edgeStyle.strokeColor}
                        fontWeight="500"
                        style={{ pointerEvents: 'none' }}
                      >
                        {edge.label || edge.type.replace('edge-', '')}
                      </text>
                      {edge.condition && (
                      <text
                        x={(adjustedSourceX + adjustedTargetX) / 2}
                        y={(adjustedSourceY + adjustedTargetY) / 2 + 14}
                        textAnchor="middle"
                        fontSize={8}
                        fill={edgeStyle.strokeColor}
                        opacity="0.7"
                        style={{ pointerEvents: 'none' }}
                      >
                        {edge.condition.length > 20 ? `${edge.condition.substring(0, 20)}...` : edge.condition}
                      </text>
                      )}
                    </g>
                    )}

                    {/* Enhanced connection points */}
                    <circle
                      cx={adjustedSourceX}
                      cy={adjustedSourceY}
                      r={isSelected ? 8 : 6}
                      fill={edgeStyle.strokeColor}
                      stroke="#fff"
                      strokeWidth={2}
                      opacity="0.8"
                      style={{
                        filter: isSelected ? 'drop-shadow(0 0 3px rgba(25, 118, 210, 0.6))' : 'none',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => handleEdgeClick(edge, e)}
                      onContextMenu={(e) => handleEdgeContextMenu(edge, e)}
                    />
                    <circle
                      cx={adjustedTargetX}
                      cy={adjustedTargetY}
                      r={isSelected ? 8 : 6}
                      fill={edgeStyle.strokeColor}
                      stroke="#fff"
                      strokeWidth={2}
                      opacity="0.8"
                      style={{
                        filter: isSelected ? 'drop-shadow(0 0 3px rgba(25, 118, 210, 0.6))' : 'none',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => handleEdgeClick(edge, e)}
                      onContextMenu={(e) => handleEdgeContextMenu(edge, e)}
                    />

                    {/* Selection indicator */}
                    {isSelected && (
                    <>
                      <rect
                        x={Math.min(adjustedSourceX, adjustedTargetX) - 5}
                        y={Math.min(adjustedSourceY, adjustedTargetY) - 5}
                        width={Math.abs(adjustedTargetX - adjustedSourceX) + 10}
                        height={Math.abs(adjustedTargetY - adjustedSourceY) + 10}
                        fill="none"
                        stroke="#1976D2"
                        strokeWidth="1"
                        strokeDasharray="3,3"
                        opacity="0.5"
                        style={{ pointerEvents: 'none' }}
                      />
                      {/* Selected edge highlight */}
                      <path
                        d={`M ${adjustedSourceX} ${adjustedSourceY} L ${adjustedTargetX} ${adjustedTargetY}`}
                        stroke="#1976D2"
                        strokeWidth="8"
                        fill="none"
                        opacity="0.3"
                        style={{ pointerEvents: 'none' }}
                      />
                    </>
                    )}

                    {/* Edge tooltip on hover */}
                    {hoveredEdge === edge.id && (
                    <foreignObject
                      x={(adjustedSourceX + adjustedTargetX) / 2 - 50}
                      y={(adjustedSourceY + adjustedTargetY) / 2 - 40}
                      width={100}
                      height={30}
                      style={{ pointerEvents: 'none' }}
                    >
                      <Box
                        sx={{
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                        }}
                      >
                        {edge.label || edge.type || 'Edge'}
                      </Box>
                    </foreignObject>
                    )}
                  </g>
                );
              }))()}

              {/* Nodes */}
              {(() =>
                // console.log('🎨 Rendering nodes:', nodes.length);
                nodes.map((node) =>
                  // console.log('🎨 Rendering node:', node.id, node.type, 'at position:', node.position);
                  (
                    <foreignObject
                      key={node.id}
                      x={node.position.x}
                      y={node.position.y}
                      width={NODE_WIDTH}
                      height={NODE_HEIGHT}
                      style={{ zIndex: 20, overflow: 'visible' }}
                    >
                      <FlowNodeMCP
                        key={node.id}
                        node={node}
                        onSelect={handleNodeClick}
                        isSelected={selectedNode?.id === node.id}
                        isConnectionStart={connectionStart?.id === node.id}
                        onDelete={handleNodeDelete}
                        onDrag={handleNodeDrag}
                        onNodeConfigUpdate={handleNodeConfigUpdate}
                        updateNode={updateNode}
                        hasBreakpoint={breakpoints.includes(node.id)}
                      />
                    </foreignObject>
                  ))
              )()}
            </g>
          </svg>
        </Box>

        {/* Footer - absolutely positioned at the bottom of the canvas container */}
        {/*
        <Paper
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 30,
            px: 2,
            py: 1,
            borderRadius: '0 0 8px 8px',
            backgroundColor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.875rem',
          }}
        >
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Nodes:
              {' '}
              {nodes.length}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Connections:
              {' '}
              {edges.length}
            </Typography>
            {selectedNode && (
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Selected:
                {' '}
                {selectedNode.data?.label || selectedNode.label}
              </Typography>
            )}
            {selectedEdge && (
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                Edge:
                {' '}
                {selectedEdge.source}
                {' '}
                →
                {' '}
                {selectedEdge.target}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {connectionStart ? '🔗 Connection Mode' : '✋ Select Mode'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {connectionStart ? 'Click target node to connect' : 'Ctrl/Cmd + Click to connect • Alt + Drag to pan • Ctrl + Scroll to zoom'}
            </Typography>
          </Box>
        </Paper>
        */}
      </Box>

      {/* Enhanced Edge Dialog */}
      <EdgeDialog
        open={edgeDialogOpen}
        edge={selectedEdge}
        nodes={nodes}
        onClose={() => {
          setEdgeDialogOpen(false);
          setSelectedEdge(null);
        }}
        onSave={(edgeId, updatedConfig) => {
          // Update edge configuration in parent state
          if (onEdgeUpdate) {
            onEdgeUpdate(edgeId, updatedConfig);
          }
          setEdgeDialogOpen(false);
          setSelectedEdge(null);
        }}
      />

      {/* Context Menu */}
      <EdgeContextMenu
        open={contextMenu.open}
        anchorEl={contextMenu.anchorEl}
        onClose={handleContextMenuClose}
        edge={contextMenu.edge}
        onEdit={handleContextMenuEdit}
        onDelete={handleContextMenuDelete}
        onCopy={handleContextMenuCopy}
      />
    </ReactFlowProvider>
  );
}

export default FlowCanvas;
